import { useCallback, useState } from 'react';
import Spinner from '../Spinner';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { Typography, Button, Box, ListItemText, Card } from '@mui/material';
import { DataGrid, GridColDef, GridInitialState } from '@mui/x-data-grid';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import { getNotificationLocale, getRowHeight } from './utils';
import ReactIntlWrapper from 'components/i18n/ReactIntlWrapper';
import { DEFAULT_PAGINATION, Pagination } from 'hooks/useSearch';
import {
  useMarkAllAsReadMutation,
  useMarkAsReadMutation,
  useMarkListAsReadMutation,
  useNotificationCount,
  useNotificationsStorage,
} from 'store/registry/useNotificationsStorage';
import { fToNow } from 'utils/format-time';
import { Iconify } from 'components/iconify';
import { usePaginationPreference } from 'hooks/usePaginationPreference';

const Notifications = () => {
  const api: Api = useApi();

  const [errors, setErrors] = useState({});
  const [listMarked, setListMarked] = useState(false);
  const [allMarked, setAllMarked] = useState(false);

  const [paginationModel, setPaginationModel] = usePaginationPreference('notifications', 10);
  const toast = useToastMessageQueue();

  const { data, isLoading } = useNotificationsStorage(
    paginationModel.page,
    paginationModel.pageSize
  );
  const { mutateAsync: markAsRead } = useMarkAsReadMutation();
  const { mutateAsync: markAllAsRead } = useMarkAllAsReadMutation();
  const { mutateAsync: markListAsRead } = useMarkListAsReadMutation();
  const {data:notificationCount} = useNotificationCount();

  const handleMarkItemAsRead = async (read: string, timeStamp: string) => {
    try {
      await markAsRead(timeStamp);
      return true;
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
      return false;
    }
  };

  const handleMarkListAsRead = async (timestamps: Array<string>) => {
    try {
      await markListAsRead(timestamps);
      setListMarked(true);
      return true;
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
      return false;
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      setAllMarked(!allMarked);
      return true;
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
      return false;
    }
  };

  const MarkAsReadButton = ({
    read,
    timeStamp,
    onMarked,
  }: {
    read: string;
    timeStamp: string;
    onMarked?: () => void;
  }) => {
    const [markedAsRead, setMarkedAsRead] = useState(read);

    const handleClick = async () => {
      if (markedAsRead === '1') return;

      const success = await handleMarkItemAsRead(markedAsRead, timeStamp);
      if (success) {
        setMarkedAsRead('1');
        onMarked?.();
      }
    };

    return (
      <Button
        onClick={handleClick}
        variant="text"
        color="primary"
        sx={{
          ...(markedAsRead === '1' && { pointerEvents: 'none' }),
        }}
      >
        {markedAsRead === '1' ? <BookmarkIcon /> : <BookmarkBorderIcon />}
      </Button>
    );
  };

  const onPaginationModelChange = useCallback(
    (model: { pageSize: number; page: number }) => {
      setPaginationModel(model);
      if (model.page !== paginationModel.page) {
        setListMarked(false);
      }
    },
    [paginationModel.page, setPaginationModel]
  );

  const INITIAL_STATE: GridInitialState = {
    pagination: {
      paginationModel: {
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
      },
    },
  };

  const columns: GridColDef[] = [
    {
      field: 'resourceId',
      headerName: ReactIntlWrapper.get('notifications'),
      flex: 2,
      sortable: false,
      renderCell: (params) => {
        const parsed = JSON.parse(params.row.message);
        const localeText = getNotificationLocale(
          parsed.message?.ResourceID,
          parsed.message?.Params || []
        );
        return (
          <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <ListItemText
              primary={localeText}
              secondary={
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    gap: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    color: 'text.disabled',
                  }}
                >
                  <Iconify width={14} icon="solar:clock-circle-outline" />
                  {fToNow(new Date(params?.row.timeStamp)) } 
                </Typography>
              }
            />
          </div>
        );
      },
    },
    {
      field: 'priority',
      headerName: ReactIntlWrapper.get('priority'),
      flex: 0,
      sortable: false,
      renderCell: (params) => {
        const parsed = JSON.parse(params.row.message);
        return ReactIntlWrapper.get(parsed.priority);
      },
    },
    {
      field: 'read',
      headerName: ReactIntlWrapper.get('markAsRead'),
      width: 150,
      sortable: false,
      renderCell: (params) => {
        return <MarkAsReadButton read={params.row.read} timeStamp={params.row.timeStamp} />;
      },
    },
  ];

  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography color="primary" variant="h2" component="h2">
            {ReactIntlWrapper.get('notifications')}
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2, gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button color="primary" variant="text" size="small">
              {listMarked ? (
                <BookmarkIcon />
              ) : (
                <BookmarkBorderIcon
                  onClick={() =>
                    handleMarkListAsRead(
                      data?.queryResult
                        ?.map((notification: any) => notification?.timeStamp)
                        ?.filter(Boolean) || []
                    )
                  }
                />
              )}
            </Button>
            <Typography variant="body2">
              {ReactIntlWrapper.get('markPage')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button color="primary" variant="text" size="small">
              {allMarked ? (
                <BookmarkIcon />
              ) : (
                <BookmarkBorderIcon onClick={() => handleMarkAllAsRead()} />
              )}
            </Button>
            <Typography variant="body2">
              {ReactIntlWrapper.get('markAll')}
            </Typography>
          </Box>
        </Box>

        {/* DataGrid */}
        <Box sx={{ width: '100%', display: 'flex' }}>
          {isLoading && !data && <Spinner />}
          {data && (
            <DataGrid
              rows={data.queryResult || []}
              getRowId={(row) => row.timeStamp}
              columns={columns}
              loading={isLoading}
              initialState={INITIAL_STATE}
              rowCount={notificationCount || 0}
              rowHeight={getRowHeight((row) => row)}
              onPaginationModelChange={onPaginationModelChange}
              paginationMode="server"
              paginationModel={paginationModel}
              pageSizeOptions={[10, 25, 50, 100]}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                '& .MuiDataGrid-row:hover': {
                  cursor: 'pointer',
                },
              }}
            />
          )}
        </Box>
      </Box>
    </Card>
  );
};

export default Notifications;
